import { useEffect, useState } from "react";
import { Check, Download, Shield, Zap, Loader2 } from "lucide-react";


const ProgressStepper = ({ onComplete }) => {

    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);


    const steps = [
        {
            id: 1,
            title: "Client verification",
            description: "Client spam check...",
            icon: Download,
            duration: 5000,
        },
        {
            id: 2,
            title: "Initializing Download",
            description: "Preparing your download request...",
            icon: Download,
            duration: 5000,
        },
        {
            id: 3,
            title: "Verifying Availability",
            description: "Checking file availability and permissions...",
            icon: Shield,
            duration: 5000,
        },
        {
            id: 4,
            title: "Download Status",
            description: "Download in progress...",
            icon: Zap,
            duration: 15000,
        },
    ];

    useEffect(() => {
        if (currentStep >= steps.length) {
            // All steps completed, trigger callback
            setTimeout(() => onComplete(), 500);
            return;
        }

        const step = steps[currentStep];
        const startTime = Date.now();

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / step.duration) * 100, 100);
            setProgress(newProgress);

            if (newProgress >= 100) {
                clearInterval(interval);
                setTimeout(() => {
                    setCurrentStep(currentStep + 1);
                    setProgress(0);
                }, 300);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [currentStep, onComplete]);


    return (
        <div className="mt-[16px]  ">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>

            <div className="max-w-2xl w-full">
                <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-blue-200">
                    {/* Header */}
                    <div className="text-center mb-[16px]">
                        <div className="inline-flex items-center justify-center w-[52px] h-[52px] rounded-xl shadow-xl mb-[8px] animate-pulse" style={{ background: "linear-gradient(135deg, #01497c, #468faf)" }}>
                            <Download className="w-[26px] h-[26px] text-white" />
                        </div>
                        <h2 className="text-gray-900 mb-[4px] text-[22px]">Preparing Your Download</h2>
                        <p className="text-gray-600 text-[14px]">Please wait while we set everything up...</p>
                    </div>

                    {/* Steps */}
                    <div className="space-y-6 mb-8">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = index === currentStep;
                            const isCompleted = index < currentStep;

                            return (
                                <div
                                    key={step.id}
                                    className={`flex items-start gap-4 transition-all duration-500 ${isActive ? 'scale-105' : 'scale-100'
                                        }`}
                                >
                                    {/* Step Icon */}
                                    <div className="flex-shrink-0">
                                        <div
                                            className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${isCompleted
                                                ? 'bg-green-500 shadow-lg shadow-green-200'
                                                : isActive
                                                    ? 'shadow-lg animate-pulse'
                                                    : 'bg-gray-100'
                                                }`}
                                            style={isActive ? {
                                                background: "linear-gradient(135deg, #01497c, #468faf)",
                                                boxShadow: "0 10px 15px -3px rgba(70, 143, 175, 0.3)"
                                            } : undefined}
                                        >
                                            {isCompleted ? (
                                                <Check className="w-7 h-7 text-white" />
                                            ) : isActive ? (
                                                <Loader2 className="w-7 h-7 text-white animate-spin" />
                                            ) : (
                                                <Icon className="w-7 h-7 text-gray-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="flex-1 min-w-0 pt-1">
                                        <h3
                                            className={`mb-1 transition-colors ${isActive || isCompleted ? 'text-gray-900' : 'text-gray-400'
                                                }`}
                                        >
                                            {step.title}
                                        </h3>
                                        <p
                                            className={`text-sm transition-colors ${isActive ? 'text-gray-600' : 'text-gray-400'
                                                }`}
                                        >
                                            {step.description}
                                        </p>

                                        {isActive &&
                                            <div className="w-full bg-gray-400 rounded-full h-3 overflow-hidden">
                                                <div
                                                    className="h-3 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: `${progress}%`,
                                                        background: "linear-gradient(135deg, #01497c, #468faf)"
                                                    }}
                                                />
                                            </div>
                                        }
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Overall Progress */}
                    <div className="rounded-2xl p-6 border" style={{
                        background: "linear-gradient(90deg, rgba(70, 143, 175, 0.1), rgba(1, 73, 124, 0.1))",
                        borderColor: "#468faf"
                    }}>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-700">Overall Progress</span>
                            <span style={{ color: "#01497c" }}>
                                {currentStep} / {steps.length} steps
                            </span>
                        </div>
                        <div className="w-full bg-gray-400 rounded-full h-3 overflow-hidden">
                            <div
                                className="h-3 rounded-full transition-all duration-300"
                                style={{
                                    width: `${(currentStep / steps.length) * 100}%`,
                                    background: "linear-gradient(135deg, #01497c, #468faf)"
                                }}
                            />
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProgressStepper